import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Asumo que esta función viene de tus utilidades
// import { trackMetaEvent } from '../utils/MetaPixel';

// Simulación de la función de tracking para demostración
const trackMetaEvent = (event, data) => {
  console.log(`Meta Pixel Event: ${event}`, data);
};

// --- Estilos en formato de objeto JavaScript ---
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#111827', // bg-gray-900
    color: '#ffffff', // text-white
    padding: '1rem',
    overflow: 'hidden',
    position: 'relative',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  },
  mainCard: {
    position: 'relative',
    zIndex: 10,
    backgroundColor: 'rgba(31, 41, 55, 0.5)', // bg-gray-800 bg-opacity-50
    backdropFilter: 'blur(10px)',
    border: '1px solid #374151', // border-gray-700
    padding: '2rem',
    borderRadius: '1rem', // rounded-2xl
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-2xl
    maxWidth: '42rem',
    width: '100%',
    textAlign: 'center',
  },
  checkIconContainer: {
    margin: '0 auto 1.5rem auto',
  },
  checkIconSvg: {
    width: '6rem', // w-24
    height: '6rem', // h-24
    color: '#22c55e', // text-green-500
  },
  mainHeading: {
    fontSize: '2.25rem', // text-4xl
    fontWeight: 'bold',
    marginBottom: '0.75rem',
    background: 'linear-gradient(to right, #4ade80, #3b82f6)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
  },
  subheading: {
    fontSize: '1.125rem', // text-lg
    color: '#d1d5db', // text-gray-300
    marginBottom: '1.5rem',
  },
  infoBox: {
    backgroundColor: 'rgba(17, 24, 39, 0.5)', // bg-gray-900/50
    padding: '1.5rem',
    borderRadius: '0.5rem', // rounded-lg
    border: '1px solid #374151', // border-gray-700
  },
  infoBoxTitle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#4ade80', // text-green-400
  },
  infoBoxText: {
    color: '#9ca3af', // text-gray-400
  },
  countdownContainer: {
    marginTop: '2rem',
    fontSize: '0.875rem', // text-sm
    color: '#6b7280', // text-gray-500
  },
  countdownText: {
    fontSize: '1.5rem', // text-2xl
    fontWeight: 'bold',
    color: '#ffffff', // text-white
    marginTop: '0.25rem',
  },
};

// Componente de Icono de Check para reutilizar
const CheckIcon = () => (
  <svg
    style={styles.checkIconSvg}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <motion.path
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    ></motion.path>
  </svg>
);

const SuccessEbook = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(15);
  const [ebookData, setEbookData] = useState(null);

  // Efecto para leer datos y disparar el píxel de Meta
  useEffect(() => {
    const storedData = localStorage.getItem("ebookData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setEbookData(parsedData);

      trackMetaEvent('Purchase', {
        value: parsedData.ebook_price,
        currency: parsedData.ebook_currency,
        content_name: parsedData.ebook_name,
        content_ids: [parsedData.ebook_id],
        status: 'success'
      });
      if(!parsedData.send_inscription){
        const apiUrl = `${process.env.REACT_APP_HOST_BACKEND}/send_ebook`;
        parsedData.student.paymentProof = null;

        const sendEbook = async () => {
          try {
            // Crear un objeto FormData
            const formDataToSend = new FormData();
            Object.entries(parsedData.student).forEach(([key, value]) => {
              formDataToSend.append(key, value);
            });

            await fetch(apiUrl, {
              method: "POST",
              body: formDataToSend,
              mode: 'cors'
            });
          } catch (error) {
            alert("Hubo un problema al generar la compra del Ebook.");
          }
        }
        sendEbook();
      }
    }
  }, []);

  // Efecto para los temporizadores
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1500);

    const redirectTimeout = setTimeout(() => {
      navigate("/courses");
    }, 20000);

    return () => {
      clearInterval(timer);
      clearTimeout(redirectTimeout);
    };
  }, [navigate]);

  return (
    <div style={styles.container}>
      {/* El fondo animado es difícil de replicar sin CSS, por lo que se ha eliminado */}
      {/* para garantizar la compatibilidad sin dependencias. */}
      
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        style={styles.mainCard}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 200, damping: 15 }}
          style={styles.checkIconContainer}
        >
          <CheckIcon />
        </motion.div>

        <h1 style={styles.mainHeading}>
          ¡Excelente Decisión, {ebookData?.student?.fullName || "Estudiante"}!
        </h1>

        <p style={styles.subheading}>
          Has invertido en tu crecimiento y estamos emocionados por ti. Tu viaje hacia el conocimiento comienza ahora.
        </p>

        <div style={styles.infoBox}>
          <h2 style={styles.infoBoxTitle}>Tu Ebook está en camino 🚀</h2>
          <p style={styles.infoBoxText}>
            Hemos enviado el <strong>"{ebookData?.ebook_name || 'Nuevo Ebook'}"</strong> a tu correo electrónico.
            <br />
            Por favor, revisa tu bandeja de entrada (y la de spam, por si acaso).
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          style={styles.countdownContainer}
        >
          <p>Serás redirigido a nuestra plataforma de cursos en...</p>
          <p style={styles.countdownText}>{countdown}</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SuccessEbook;
